import React from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, createRefetchContainer } from 'react-relay';
import serverVars from 'server-vars';
import { ServerVarsContext } from '../global/ServerVarsContext/ServerVarsContext';

// components
import { PdpRespContainer } from './PdpRespContainer/PdpRespContainer';
import { PdpSharedLayout } from './PdpSharedLayout';

// styles
import './styles/PdpRespLayout.scss';

export class PdpRespLayoutComponent extends React.Component {
    componentDidMount() {
        if (this.props.isPdpRespPreviewPage) {
            document.body.classList.add('disable-click-on-preview');
        }
    }

    getAdditionalTrackingData() {
        const seoAbTestTrackingData = serverVars.get('seoAbTestTrackingData') || null;
        const additional = {
            isCondensed: 'N',
            seoAbTestTrackingData,
        };

        return additional;
    }

    render() {
        const { relayVariables } = this.props;
        const viewer = this.props?.viewer || {};
        const user = viewer.user || null;
        const item = viewer.itemRead?.[0] || null;
        const relevantShipping = viewer.relevantShipping || {};

        const sharedProps = {
            relayVariables,
            viewer,
            user,
            item,
            similarGridItems: viewer?.similarGridItems || null,
        };
        const isPdpMetaDescRemovalVariant = this.context.isPdpMetaDescRemovalVariant;

        return (
            <PdpSharedLayout
                user={user}
                viewer={viewer}
                refreshPageOnTradeLogin={false}
                additionalTrackingData={this.getAdditionalTrackingData()}
                doRefetch={this.props.relay.refetch}
            >
                {({ pdpHead, currency, onSkuChange, selectedSkuId }) => (
                    <React.Fragment>
                        <Helmet
                            {...pdpHead.getPdpData({
                                hideDescription: isPdpMetaDescRemovalVariant,
                            })}
                        />
                        <PdpRespContainer
                            {...sharedProps}
                            currency={currency}
                            onSkuChange={onSkuChange}
                            buyerItemConversation={viewer.buyerItemConversation || null}
                            displayInfo={item?.displayInfo || null}
                            selectedSkuId={selectedSkuId}
                            similarLandingItems={viewer.similarLandingItems || null}
                            vipUserRelationship={viewer.vipUserRelationships?.[0] || null}
                            shipmentQuote={relevantShipping.shipmentQuote || null}
                            shipment={relevantShipping.shipment || null}
                        />
                    </React.Fragment>
                )}
            </PdpSharedLayout>
        );
    }
}

PdpRespLayoutComponent.propTypes = {
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        userIds: PropTypes.arrayOf(PropTypes.string),
        itemId: PropTypes.string.isRequired,
        itemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
        isClient: PropTypes.bool.isRequired,
        isMultiSku: PropTypes.bool.isRequired,
        fetchUser: PropTypes.bool.isRequired,
        followSearchPages: PropTypes.array,
        fetchFollowSearch: PropTypes.bool,
        showSimilarItems: PropTypes.bool,
    }).isRequired,
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,

    // from redux
    isPdpRespPreviewPage: PropTypes.bool,
    showSimilarItemsTop: PropTypes.func,
};

PdpRespLayoutComponent.contextType = ServerVarsContext;

// Redux Map Functions
function mapStateToProps({ pdpPreviewPage, relayVariables }) {
    return {
        isPdpRespPreviewPage: pdpPreviewPage.isPdpRespPreviewPage,
        relayVariables: relayVariables.variables,
    };
}

export const PdpRespLayout = createRefetchContainer(
    connect(mapStateToProps)(PdpRespLayoutComponent),
    {
        viewer: graphql`
            fragment PdpRespLayout_viewer on Viewer {
                ...PdpRespContainer_viewer
                ...PdpSharedLayout_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    ...PdpRespContainer_user
                    ...PdpSharedLayout_user
                }
                itemRead(itemIds: $itemIds) {
                    ... on Item {
                        ...PdpRespContainer_item
                        displayInfo(page: pdp, buyerId: $userId) @include(if: $isClient) {
                            ...PdpRespContainer_displayInfo
                        }
                    }
                }
                ... @include(if: $fetchPdp) {
                    similarLandingItems: moreLikeThisItem(first: 12, itemId: $itemId) {
                        ...PdpRespContainer_similarLandingItems
                    }
                }
                relevantShipping(itemId: $itemId, buyerId: $userId) {
                    shipmentQuote {
                        ...PdpRespContainer_shipmentQuote
                    }
                    shipment {
                        ...PdpRespContainer_shipment
                    }
                }
            }
        `,
    },
    graphql`
        query PdpRespLayoutRefetchQuery(
            $isClient: Boolean!
            $itemId: String!
            $itemIds: [String]!
            $userId: String = ""
            $userIds: [String]
            $fetchUser: Boolean!
            $followSearchPages: [String]
            $fetchFollowSearch: Boolean!
            $isTrade: Boolean!
            $isMultiSku: Boolean!
            $fetchPdp: Boolean!
            $forcedCurrency: Currency
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $localRecentHistoryItemIds: [String] = []
            $fetchUserRecentHistoryItem: Boolean = false
            $isStoryTellingModuleVariant: Boolean!
            $isInteractivePdpChat: Boolean = true
        ) {
            viewer {
                ...PdpRespLayout_viewer
            }
        }
    `
);
